import React, { useState, useEffect } from "react";
import {
  Modal,
  Input,
  Button,
  Spin,
  message,
  Tooltip,
  Divider,
  Typography,
} from "antd";
import {
  CopyOutlined,
  FileTextOutlined,
  PrinterOutlined,
} from "@ant-design/icons";
import { generateTruckTarjaTextbyId } from "../../../../libs/apis";

const { Title, Text } = Typography;
const { TextArea } = Input;
// Interfaz para las props del componente
interface TarjaTextModalProps {
  visible: boolean;
  onClose: () => void;
  containerId: number;
  containerCode: string;
  isLoading: boolean;
  tarjaText: string;
}

const TarjaTextModal: React.FC<TarjaTextModalProps> = ({
  visible,
  onClose,
  containerCode,
  isLoading,
  tarjaText,
}) => {
  const [copied, setCopied] = useState<boolean>(false);
  const [prettyText, setPrettyText] = useState<JSX.Element[]>([]);

  // Procesar texto para mejor visualización cuando cambia
  useEffect(() => {
    if (!tarjaText) {
      setPrettyText([]);
      return;
    }

    // Convertir el texto plano a JSX con formato
    const sections = tarjaText.split("----------------------------------");
    const processedSections = sections
      .map((section, index) => {
        const lines = section.split("\n").filter((line) => line.trim());

        if (lines.length === 0) return null;

        // Detectar encabezado de sección
        const title = lines[0].trim();
        const content = lines.slice(1).map((line, lineIndex) => {
          // Dividir cada línea en clave y valor
          const parts = line.split(":");
          if (parts.length >= 2) {
            const key = parts[0].trim();
            const value = parts.slice(1).join(":").trim();
            return (
              <div
                key={`line-${index}-${lineIndex}`}
                style={{ display: "flex", marginBottom: "4px" }}
              >
                <Text strong style={{ minWidth: "180px" }}>
                  {key}:
                </Text>
                <Text>{value}</Text>
              </div>
            );
          }
          return <div key={`line-${index}-${lineIndex}`}>{line}</div>;
        });

        return (
          <div key={`section-${index}`} style={{ marginBottom: "16px" }}>
            {title && title.includes("=") ? (
              <Title
                level={4}
                style={{ textAlign: "center", marginBottom: "16px" }}
              >
                {title.replace(/=/g, "")}
              </Title>
            ) : (
              <Title level={5} style={{ marginBottom: "8px" }}>
                {title}
              </Title>
            )}
            <div style={{ marginLeft: "12px" }}>{content}</div>
            {index < sections.length - 1 && (
              <Divider style={{ margin: "12px 0" }} />
            )}
          </div>
        );
      })
      .filter(Boolean);

    setPrettyText(processedSections as JSX.Element[]);
  }, [tarjaText]);

  // Función para copiar el texto al portapapeles
  const copyToClipboard = () => {
    navigator.clipboard.writeText(tarjaText).then(
      () => {
        setCopied(true);
        message.success("Texto copiado al portapapeles");
        setTimeout(() => setCopied(false), 2000);
      },
      () => {
        message.error("No se pudo copiar el texto");
      }
    );
  };

  // Función para imprimir solo el texto
  const printText = () => {
    const printWindow = window.open("", "_blank");
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>Tarja de Contenedor ${containerCode}</title>
            <style>
            html, body {
              font-family: monospace;
              white-space: pre;
              margin: 0 !important;
              padding: 0 !important;
              height: 100%;
            }
            body {
              display: flex;
              flex-direction: column;
              align-items: flex-start;
            }
            @media print {
              html, body {
                font-size: 12px;
                margin: 0 !important;
                padding: 0 !important;
                height: 100%;
              }
              body { line-height: 1; }
            }
          </style>
          </head>
          <body>
            ${tarjaText.replace(/\n/g, "<br/>").replaceAll("=","")}
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    }
  };

  return (
    <Modal
      title={`Texto de Tarja - Contenedor ${containerCode}`}
      open={visible}
      onCancel={onClose}
      width={800}
      centered
      zIndex={1101}
      footer={[
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <Button key="close" onClick={onClose} danger>
            Cerrar
          </Button>
          <Button
            key="print"
            icon={<PrinterOutlined />}
            onClick={printText}
            disabled={isLoading || !tarjaText}
          >
            Imprimir
          </Button>
          <Button
            key="copy"
            type="primary"
            icon={<CopyOutlined />}
            onClick={copyToClipboard}
            disabled={isLoading || !tarjaText}
          >
            {copied ? "Copiado!" : "Copiar Texto"}
          </Button>
        </div>,
      ]}
    >
      <div style={{ height: "calc(100vh - 160px)", overflowY: "auto" }}>
        {isLoading ? (
          <div style={{ textAlign: "center", padding: "20px" }}>
            <Spin tip="Cargando texto de la tarja..." />
          </div>
        ) : (
          <div>
            {/* Vista formateada */}
            <div style={{ marginBottom: "16px" }}>
              {prettyText.length > 0 ? (
                <div
                  style={{
                    border: "1px solid #f0f0f0",
                    padding: "16px",
                    borderRadius: "4px",
                  }}
                >
                  {prettyText}
                </div>
              ) : (
                <div style={{ textAlign: "center", padding: "20px" }}>
                  {tarjaText
                    ? "Procesando texto..."
                    : "No hay texto disponible"}
                </div>
              )}
            </div>

            {/* Texto plano en textarea para facilitar la copia */}
            <Divider>Texto Plano</Divider>
            <TextArea
              value={tarjaText}
              autoSize={{ minRows: 10, maxRows: 20 }}
              readOnly
              style={{
                width: "100%",
                marginBottom: "10px",
                fontFamily: "monospace",
              }}
            />
          </div>
        )}
      </div>
    </Modal>
  );
};

const ComponentModalTarjaText = ({
  containerId,
  containerCode,
  isOffline,
}: {
  containerId: number;
  containerCode: string;
  isOffline: boolean;
}) => {
  const [tarjaTextModalVisible, setTarjaTextModalVisible] =
    React.useState<boolean>(false);
  const [tarjaText, setTarjaText] = React.useState<string>("");
  const [isLoadingTarjaText, setIsLoadingTarjaText] =
    React.useState<boolean>(false);

  // 3. Añadir la función para obtener el texto de la tarja
  const handleTarjaText = async (): Promise<void> => {
    // Validar si estamos en modo offline
    if (isOffline) {
      message.warning("No se puede obtener el texto de tarja sin conexión", 3);
      return;
    }

    try {
      setIsLoadingTarjaText(true);
      setTarjaText("");

      // Obtener el texto de la tarja
      const { data: text } = await generateTruckTarjaTextbyId(containerId!);
      setTarjaText(text);
      setTarjaTextModalVisible(true);
    } catch (err) {
      message.error(
        `Error al obtener el texto de la tarja: ${
          err instanceof Error ? err.toString() : "Error desconocido"
        }`
      );
      setTarjaText("Error al cargar el texto de la tarja");
    } finally {
      setIsLoadingTarjaText(false);
    }
  };

  // 4. Añadir función para cerrar el modal
  const handleCloseTarjaTextModal = (): void => {
    setTarjaText("");
    setTarjaTextModalVisible(false);
  };

  return (
    <>
      {tarjaTextModalVisible && (
        <TarjaTextModal
          visible={tarjaTextModalVisible}
          onClose={handleCloseTarjaTextModal}
          containerId={containerId}
          containerCode={containerCode}
          isLoading={isLoadingTarjaText}
          tarjaText={tarjaText}
        />
      )}
      <Tooltip arrow={false} title="Texto de Tarja">
        <Button
          loading={isLoadingTarjaText}
          disabled={isLoadingTarjaText || isOffline}
          size="small"
          type="dashed"
          onClick={() => handleTarjaText()}
          icon={<FileTextOutlined />}
        />
      </Tooltip>
    </>
  );
};

export default ComponentModalTarjaText;
